import { useEffect, useRef, useState } from "react";
import { WhatsAppContact } from "@/components/WhatsAppContact";
import { useParams } from "react-router-dom";
import axios from "axios";
import { LOGO_ROUND, FAVICON, houseCoverUrl } from "@/lib/constants";
import { InstallPWA } from "@/components/InstallPWA";
import { GuestPushButton } from "@/components/GuestPushButton";
import { BookOpen, Loader2, ExternalLink, CalendarCheck, MessageCircle, Send, Paperclip, FileText, MapPin, Languages } from "lucide-react";
import { formatDate, formatDateTime } from "@/lib/date";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LANGS = { nl: "Nederlands", en: "English", de: "Deutsch", fr: "Français", es: "Español" };
const FLAG = { nl: "🇳🇱", en: "🇬🇧", de: "🇩🇪", fr: "🇫🇷", es: "🇪🇸" };
const ALL_LANGS = ["nl", "en", "de", "fr", "es"];

const T = {
  nl: { welcome: "Welkom", bookHeading: "Welkomstboek:", yourBook: "Jouw taal", chatTitle: "Chat met Costa Key",
        chatEmpty: "Heb je een vraag over je verblijf? Stuur ons gerust een bericht — we reageren zo snel mogelijk.",
        placeholder: "Typ je bericht…", notReady: "Het welkomstboek voor deze woning wordt nog voorbereid.",
        footer: "Fijn verblijf! · Costa Key — Sleutel- & Woningbeheer", you: "Jij", team: "Costa Key",
        translate: "Vertaal", showOriginal: "Toon origineel", notFound: "Welkomstboek niet gevonden of niet meer beschikbaar.",
        waText: "Voor dringende zaken zijn we het best bereikbaar via een WhatsApp-oproep:" },
  en: { welcome: "Welcome", bookHeading: "Welcome book:", yourBook: "Your language", chatTitle: "Chat with Costa Key",
        chatEmpty: "Have a question about your stay? Send us a message — we'll reply as soon as possible.",
        placeholder: "Type your message…", notReady: "The welcome book for this property is being prepared.",
        footer: "Enjoy your stay! · Costa Key — Key & Property Management", you: "You", team: "Costa Key",
        translate: "Translate", showOriginal: "Show original", notFound: "Welcome book not found or no longer available.",
        waText: "For urgent matters, the best way to reach us is via a WhatsApp call:" },
  de: { welcome: "Willkommen", bookHeading: "Willkommensbuch:", yourBook: "Ihre Sprache", chatTitle: "Chat mit Costa Key",
        chatEmpty: "Haben Sie eine Frage zu Ihrem Aufenthalt? Schreiben Sie uns — wir antworten so schnell wie möglich.",
        placeholder: "Nachricht eingeben…", notReady: "Das Willkommensbuch für diese Unterkunft wird noch vorbereitet.",
        footer: "Schönen Aufenthalt! · Costa Key — Schlüssel- & Immobilienverwaltung", you: "Sie", team: "Costa Key",
        translate: "Übersetzen", showOriginal: "Original anzeigen", notFound: "Willkommensbuch nicht gefunden oder nicht mehr verfügbar.",
        waText: "In dringenden Fällen erreichen Sie uns am besten per WhatsApp-Anruf:" },
  fr: { welcome: "Bienvenue", bookHeading: "Livret d'accueil :", yourBook: "Votre langue", chatTitle: "Chat avec Costa Key",
        chatEmpty: "Une question sur votre séjour ? Envoyez-nous un message — nous répondrons dès que possible.",
        placeholder: "Écrivez votre message…", notReady: "Le livret d'accueil de ce logement est en cours de préparation.",
        footer: "Bon séjour ! · Costa Key — Gestion de clés & de biens", you: "Vous", team: "Costa Key",
        translate: "Traduire", showOriginal: "Voir l'original", notFound: "Livret d'accueil introuvable ou non disponible.",
        waText: "Pour les urgences, le meilleur moyen de nous joindre est un appel WhatsApp :" },
  es: { welcome: "Bienvenido", bookHeading: "Guía de bienvenida:", yourBook: "Tu idioma", chatTitle: "Chat con Costa Key",
        chatEmpty: "¿Tienes alguna pregunta sobre tu estancia? Envíanos un mensaje — responderemos lo antes posible.",
        placeholder: "Escribe tu mensaje…", notReady: "La guía de bienvenida de esta vivienda se está preparando.",
        footer: "¡Feliz estancia! · Costa Key — Gestión de llaves y propiedades", you: "Tú", team: "Costa Key",
        translate: "Traducir", showOriginal: "Ver original", notFound: "Guía de bienvenida no encontrada o ya no disponible.",
        waText: "Para asuntos urgentes, la mejor forma de contactarnos es con una llamada de WhatsApp:" },
};

const fmtChatTime = (iso) => formatDateTime(iso);

// Intake-vragenlijst (automatisch gesprek bij aankomst) — meertalig
const WZ = {
  nl: { intro: "Welkom! We stellen jullie een paar korte vragen zodat de aankomst vlot verloopt.",
    arrival: "Hoe laat verwachten jullie bij de woning aan te komen? (mogelijk tussen {from} en 22:00)",
    departure: "Hoe laat verwachten jullie de woning te verlaten? (mogelijk tussen 07:00 en {until})",
    transport: "Hoe komen jullie naar Spanje?", car: "Auto", plane: "Vliegtuig",
    transfer: "Hebben jullie een taxi of huurauto vanaf het vliegveld?", taxi: "Taxi", rental: "Huurauto",
    flightIn: "Wat is de tijd van landen in Alicante? (vermeld eventueel ook het vluchtnummer)",
    flightOut: "Wat is de tijd van opstijgen vanaf Alicante? (vermeld eventueel ook het vluchtnummer)",
    closePlane: "Wil je ons een berichtje sturen zodra jullie vanaf Alicante richting de woning vertrekken, met de verwachte aankomsttijd?",
    closeCar: "Willen jullie ons onderweg op de hoogte houden wanneer jullie aankomsttijd zal veranderen?",
    bon: "Goede reis alvast en graag tot dan! 🔆\n(Klik bovenaan deze pagina op het adres voor de exacte locatie van ons 'meetingpoint')",
    send: "Doen we! 👍", pick: "Kies een tijd", next: "Volgende", flightPh: "bijv. 14:30, VY1234", timePh: "00:00" },
  en: { intro: "Welcome! We'll ask a few quick questions so your arrival goes smoothly.",
    arrival: "What time do you expect to arrive at the property? (possible between {from} and 22:00)",
    departure: "What time do you expect to leave the property? (possible between 07:00 and {until})",
    transport: "How are you travelling to Spain?", car: "Car", plane: "Plane",
    transfer: "Do you have a taxi or a rental car from the airport?", taxi: "Taxi", rental: "Rental car",
    flightIn: "What time do you land in Alicante? (feel free to add the flight number)",
    flightOut: "What time do you take off from Alicante? (feel free to add the flight number)",
    closePlane: "Would you send us a message as soon as you leave Alicante towards the property, with your expected arrival time?",
    closeCar: "Would you keep us posted on the way if your arrival time changes?",
    bon: "Have a great trip and see you soon! 🔆\n(Click the address at the top of this page for the exact location of our 'meeting point')",
    send: "Will do! 👍", pick: "Choose a time", next: "Next", flightPh: "e.g. 14:30, VY1234", timePh: "00:00" },
  de: { intro: "Willkommen! Wir stellen Ihnen ein paar kurze Fragen, damit Ihre Ankunft reibungslos verläuft.",
    arrival: "Wann werden Sie voraussichtlich an der Unterkunft ankommen? (möglich zwischen {from} und 22:00)",
    departure: "Wann werden Sie die Unterkunft voraussichtlich verlassen? (möglich zwischen 07:00 und {until})",
    transport: "Wie reisen Sie nach Spanien an?", car: "Auto", plane: "Flugzeug",
    transfer: "Haben Sie ein Taxi oder einen Mietwagen ab dem Flughafen?", taxi: "Taxi", rental: "Mietwagen",
    flightIn: "Um wie viel Uhr landen Sie in Alicante? (gerne auch mit Flugnummer)",
    flightOut: "Um wie viel Uhr starten Sie ab Alicante? (gerne auch mit Flugnummer)",
    closePlane: "Würden Sie uns eine Nachricht schicken, sobald Sie von Alicante zur Unterkunft aufbrechen, mit Ihrer voraussichtlichen Ankunftszeit?",
    closeCar: "Würden Sie uns unterwegs informieren, falls sich Ihre Ankunftszeit ändert?",
    bon: "Gute Reise und bis bald! 🔆\n(Klicken Sie oben auf dieser Seite auf die Adresse für den genauen Standort unseres 'Treffpunkts')",
    send: "Machen wir! 👍", pick: "Uhrzeit wählen", next: "Weiter", flightPh: "z. B. 14:30, VY1234", timePh: "00:00" },
  fr: { intro: "Bienvenue ! Quelques brèves questions pour faciliter votre arrivée.",
    arrival: "À quelle heure pensez-vous arriver au logement ? (possible entre {from} et 22:00)",
    departure: "À quelle heure pensez-vous quitter le logement ? (possible entre 07:00 et {until})",
    transport: "Comment venez-vous en Espagne ?", car: "Voiture", plane: "Avion",
    transfer: "Avez-vous un taxi ou une voiture de location depuis l'aéroport ?", taxi: "Taxi", rental: "Voiture de location",
    flightIn: "À quelle heure atterrissez-vous à Alicante ? (indiquez éventuellement le numéro de vol)",
    flightOut: "À quelle heure décollez-vous d'Alicante ? (indiquez éventuellement le numéro de vol)",
    closePlane: "Pourriez-vous nous envoyer un message dès votre départ d'Alicante vers le logement, avec votre heure d'arrivée prévue ?",
    closeCar: "Pourriez-vous nous tenir informés en route si votre heure d'arrivée change ?",
    bon: "Bon voyage et à bientôt ! 🔆\n(Cliquez sur l'adresse en haut de cette page pour l'emplacement exact de notre « point de rendez-vous »)",
    send: "C'est noté ! 👍", pick: "Choisir une heure", next: "Suivant", flightPh: "p. ex. 14:30, VY1234", timePh: "00:00" },
  es: { intro: "¡Bienvenido! Te haremos unas breves preguntas para que tu llegada sea fluida.",
    arrival: "¿A qué hora esperáis llegar a la vivienda? (posible entre las {from} y las 22:00)",
    departure: "¿A qué hora esperáis salir de la vivienda? (posible entre las 07:00 y las {until})",
    transport: "¿Cómo venís a España?", car: "Coche", plane: "Avión",
    transfer: "¿Tenéis taxi o coche de alquiler desde el aeropuerto?", taxi: "Taxi", rental: "Coche de alquiler",
    flightIn: "¿A qué hora aterrizáis en Alicante? (indicad también el número de vuelo si queréis)",
    flightOut: "¿A qué hora despegáis desde Alicante? (indicad también el número de vuelo si queréis)",
    closePlane: "¿Nos enviaríais un mensaje en cuanto salgáis de Alicante hacia la vivienda, con la hora de llegada prevista?",
    closeCar: "¿Nos mantendríais informados por el camino si cambia vuestra hora de llegada?",
    bon: "¡Buen viaje y hasta pronto! 🔆\n(Haz clic en la dirección en la parte superior de esta página para ver la ubicación exacta de nuestro 'punto de encuentro')",
    send: "¡Hecho! 👍", pick: "Elige una hora", next: "Siguiente", flightPh: "p. ej. 14:30, VY1234", timePh: "00:00" },
};

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const fmtTime = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 4);
  return d.length > 2 ? `${d.slice(0, 2)}:${d.slice(2)}` : d;
};

export default function PublicWelcome() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [lang, setLang] = useState("nl");
  const [book, setBook] = useState(null);
  const [chat, setChat] = useState(null);
  const [chatText, setChatText] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const [chatUploading, setChatUploading] = useState(false);
  const chatBoxRef = useRef(null);
  const chatInit = useRef({ done: false, unread: null });
  const chatAttachRef = useRef(null);
  const [langTouched, setLangTouched] = useState(false);
  const [xlate, setXlate] = useState({});
  const [intake, setIntake] = useState(undefined);
  const [times, setTimes] = useState({ from: "16:00", until: "10:00" });
  const [isGuestBooking, setIsGuestBooking] = useState(true);
  const [wiz, setWiz] = useState({});
  const [wizSubmitting, setWizSubmitting] = useState(false);
  const [flightInDraft, setFlightInDraft] = useState("");
  const [flightOutDraft, setFlightOutDraft] = useState("");
  const [arrDraft, setArrDraft] = useState("");
  const [depDraft, setDepDraft] = useState("");

  useEffect(() => { if (data?.language && !langTouched) setLang(data.language); }, [data?.language]);

  const translateMsg = async (id) => {
    if (xlate[id]?.text) { setXlate((p) => ({ ...p, [id]: { ...p[id], show: !p[id].show } })); return; }
    setXlate((p) => ({ ...p, [id]: { ...(p[id] || {}), loading: true } }));
    try {
      const { data: r } = await axios.post(`${API}/public/chat/${token}/translate`, null, { params: { message_id: id } });
      setXlate((p) => ({ ...p, [id]: { text: r.translation, show: true, loading: false } }));
    } catch { setXlate((p) => ({ ...p, [id]: { ...(p[id] || {}), loading: false } })); }
  };

  useEffect(() => {
    axios.get(`${API}/public/welcome/${token}`).then(({ data }) => setData(data)).catch(() => setErr(true));
  }, [token]);

  const loadChat = () => axios.get(`${API}/public/chat/${token}`).then(({ data }) => {
    if (!chatInit.current.loaded) chatInit.current = { done: false, loaded: true, unread: data.first_unread_id || null };
    setChat(data.messages || []);
    setIntake(data.intake ?? null);
    setTimes({ from: data.checkin_from || "16:00", until: data.checkout_until || "10:00" });
    setIsGuestBooking(data.is_guest_booking !== false);
  }).catch(() => setChat([]));
  useEffect(() => {
    loadChat();
    const id = setInterval(loadChat, 5000);
    return () => clearInterval(id);
  }, [token]);

  // Pagina altijd bovenaan laden; chatvenster zelf naar het laatste bericht scrollen.
  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    const el = chatBoxRef.current;
    if (!el || !chat) return;
    const c = chatInit.current;
    const target = !c.done && c.unread && el.querySelector(`[data-testid="guest-chat-msg-${c.unread}"]`);
    el.scrollTop = target ? el.scrollTop + target.getBoundingClientRect().top - el.getBoundingClientRect().top - 8 : el.scrollHeight;
    if (target || chat.length) c.done = true;
  }, [chat?.length, wiz, !!intake, !!data]);

  const sendChat = async () => {
    const t = chatText.trim();
    if (!t) return;
    setChatSending(true);
    try {
      await axios.post(`${API}/public/chat/${token}`, { text: t });
      setChatText("");
      await loadChat();
    } catch { /* ignore */ }
    finally { setChatSending(false); }
  };

  // Opbouw van het intake-gesprek: [afzender, tekst] tot en met de eerstvolgende open vraag.
  const wizSteps = (w) => {
    const L = [["team", w.intro], ["team", w.arrival.replace("{from}", times.from)]];
    if (!wiz.arrival_time) return { L, ask: "arrival" };
    L.push(["guest", wiz.arrival_time], ["team", w.departure.replace("{until}", times.until)]);
    if (!wiz.departure_time) return { L, ask: "departure" };
    L.push(["guest", wiz.departure_time], ["team", w.transport]);
    if (!wiz.transport) return { L, ask: "transport" };
    L.push(["guest", wiz.transport === "plane" ? w.plane : w.car]);
    if (wiz.transport === "plane") {
      L.push(["team", w.transfer]);
      if (!wiz.airport_transfer) return { L, ask: "transfer" };
      L.push(["guest", wiz.airport_transfer === "taxi" ? w.taxi : w.rental], ["team", w.flightIn]);
      if (!wiz.flight_in) return { L, ask: "flightin" };
      L.push(["guest", wiz.flight_in], ["team", w.flightOut]);
      if (!wiz.flight_out) return { L, ask: "flightout" };
      L.push(["guest", wiz.flight_out]);
    }
    L.push(["team", wiz.transport === "plane" ? w.closePlane : w.closeCar]);
    return { L, ask: "finish" };
  };

  const submitIntake = async () => {
    const w = WZ[lang] || WZ.nl;
    const transcript = [...wizSteps(w).L, ["guest", w.send]].map(([sender, text]) => ({ sender, text }));
    setWizSubmitting(true);
    try {
      await axios.post(`${API}/public/chat/${token}/intake`, {
        arrival_time: wiz.arrival_time, departure_time: wiz.departure_time,
        transport: wiz.transport, airport_transfer: wiz.airport_transfer || null,
        flight_in: wiz.flight_in || "", flight_out: wiz.flight_out || "",
        lang, closing_message: w.bon, transcript,
      });
      await loadChat();
    } catch { /* ignore */ }
    finally { setWizSubmitting(false); }
  };

  const uploadChatAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setChatUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (chatText.trim()) fd.append("text", chatText.trim());
      await axios.post(`${API}/public/chat/${token}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setChatText("");
      await loadChat();
    } catch { /* ignore */ }
    finally { setChatUploading(false); if (chatAttachRef.current) chatAttachRef.current.value = ""; }
  };

  if (err) return (
    <div className="min-h-screen grid place-items-center bg-background text-center p-6">
      <div><BookOpen className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-40" /><p className="text-muted-foreground">{(T[lang] || T.nl).notFound}</p></div>
    </div>
  );
  if (!data) return <div className="min-h-screen grid place-items-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const ui = T[lang] || T.en;

  const renderWizard = () => {
    const w = WZ[lang] || WZ.nl;
    const bot = (txt, k) => <div key={k} className="flex justify-start"><div className="max-w-[85%] rounded-2xl rounded-bl-sm px-3.5 py-2 text-base bg-card border border-border whitespace-pre-line">{txt}</div></div>;
    const me = (txt, k) => <div key={k} className="flex justify-end"><div className="max-w-[85%] rounded-2xl rounded-br-sm px-3.5 py-2 text-base bg-primary text-primary-foreground">{txt}</div></div>;
    const timeInput = (val, setVal, onNext, tid) => {
      const ok = TIME_RE.test(val);
      return (
        <div key={tid} className="flex justify-start w-full"><div className="flex gap-2 w-full max-w-[85%]" data-testid={tid}>
          <input value={val} onChange={(e) => setVal(fmtTime(e.target.value))} onKeyDown={(e) => { if (e.key === "Enter" && ok) onNext(); }} inputMode="numeric" maxLength={5} placeholder={w.timePh} className="w-28 h-10 rounded-md border border-border bg-background px-3 text-base outline-none focus:ring-1 focus:ring-ring tabular-nums" data-testid={`${tid}-input`} />
          <button type="button" onClick={onNext} disabled={!ok} className="h-10 px-3 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50 shrink-0" data-testid={`${tid}-next`}>{w.next}</button>
        </div></div>
      );
    };
    const choice = (opts, onPick, tid) => (
      <div key={tid} className="flex justify-start"><div className="flex flex-wrap gap-2" data-testid={tid}>
        {opts.map(([val, label]) => <button key={val} type="button" onClick={() => onPick(val)} className="px-4 py-2 rounded-md border border-primary/40 text-primary text-sm font-medium hover:bg-primary hover:text-primary-foreground transition-colors" data-testid={`${tid}-${val}`}>{label}</button>)}
      </div></div>
    );
    const textInput = (val, setVal, onNext, tid) => (
      <div key={tid} className="flex justify-start w-full"><div className="flex gap-2 w-full max-w-[85%]">
        <input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && val.trim()) onNext(); }} placeholder={w.flightPh} className="flex-1 h-10 rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-ring" data-testid={`${tid}-input`} />
        <button type="button" onClick={onNext} disabled={!val.trim()} className="h-10 px-3 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50 shrink-0" data-testid={`${tid}-next`}>{w.next}</button>
      </div></div>
    );
    const finishBtn = (
      <div key="finish" className="flex justify-end"><button type="button" onClick={submitIntake} disabled={wizSubmitting} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50" data-testid="intake-submit">{wizSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{w.send}</button></div>
    );
    const set = (k) => (v) => setWiz((p) => ({ ...p, [k]: v }));
    const inputs = {
      arrival: () => timeInput(arrDraft, setArrDraft, () => set("arrival_time")(arrDraft), "wiz-arrival"),
      departure: () => timeInput(depDraft, setDepDraft, () => set("departure_time")(depDraft), "wiz-departure"),
      transport: () => choice([["car", w.car], ["plane", w.plane]], set("transport"), "wiz-transport"),
      transfer: () => choice([["taxi", w.taxi], ["rental", w.rental]], set("airport_transfer"), "wiz-transfer"),
      flightin: () => textInput(flightInDraft, setFlightInDraft, () => set("flight_in")(flightInDraft.trim()), "wiz-flightin"),
      flightout: () => textInput(flightOutDraft, setFlightOutDraft, () => set("flight_out")(flightOutDraft.trim()), "wiz-flightout"),
      finish: () => finishBtn,
    };
    const { L, ask } = wizSteps(w);
    const els = L.map(([snd, txt], i) => (snd === "guest" ? me(txt, `s${i}`) : bot(txt, `s${i}`)));
    els.push(inputs[ask]());
    return <div className="space-y-3" data-testid="intake-wizard">{els}</div>;
  };
  const i18n = data.welcome_content_i18n || {};
  const contentLangs = data.welcome_content?.sections?.length ? ["nl", ...Object.keys(i18n)] : Object.keys(i18n);
  const active = contentLangs.includes(lang) ? lang : (contentLangs[0] || "nl");
  const secs = active === "nl" ? (data.welcome_content?.sections || []) : (i18n[active]?.sections || []);
  const books = (data.welcome_books || []).slice().sort((a, b) => (b.language === lang ? 1 : 0) - (a.language === lang ? 1 : 0));

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-secondary text-secondary-foreground">
        <div className="max-w-3xl mx-auto px-5 py-8">
          <div className="flex items-center justify-between gap-2.5 mb-4">
            <div className="flex items-center gap-2.5">
              <span className="grid place-items-center h-10 w-10 rounded-full bg-white overflow-hidden shrink-0">
                <img src={FAVICON} alt="Costa Key" className="h-10 w-10 object-cover" />
              </span>
              <span className="font-display font-bold text-lg">Costa Key</span>
            </div>
            <div className="flex items-center gap-1" data-testid="lang-switcher">
              {ALL_LANGS.map((l) => (
                <button key={l} onClick={() => { setLang(l); setLangTouched(true); }} title={LANGS[l]}
                  className={`h-8 w-8 grid place-items-center rounded-full text-base transition-all ${lang === l ? "bg-white/25 ring-2 ring-white/70" : "hover:bg-white/15 opacity-80"}`}
                  data-testid={`lang-btn-${l}`}>{FLAG[l]}</button>
              ))}
            </div>
          </div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">{ui.welcome}{data.guest_name ? `, ${data.guest_name}` : ""}!</h1>
          <p className="mt-2 text-white/90 text-lg">{data.house_name}</p>
          {data.address_text && (
            <a href={data.address_link || undefined} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1.5 text-white/85 text-sm hover:text-white hover:underline w-fit" data-testid="public-address-link"><MapPin className="h-4 w-4 shrink-0" /> {data.address_text}</a>
          )}
          {data.arrival && <p className="mt-1 text-white/80 text-sm flex items-center gap-1.5"><CalendarCheck className="h-4 w-4" /> {formatDate(data.arrival)} → {formatDate(data.departure)}</p>}
          <InstallPWA lang={lang} />
          <GuestPushButton token={token} lang={lang} />
        </div>
      </header>

      {houseCoverUrl(data, "") && (
        <div className="max-w-3xl mx-auto px-5 -mt-6 relative z-10">
          <div className="rounded-2xl overflow-hidden border border-border shadow-md aspect-[21/9] bg-muted">
            <img src={houseCoverUrl(data, "")} alt={data.house_name} className="w-full h-full object-cover" data-testid="public-cover-image" />
          </div>
        </div>
      )}

      <main className="max-w-3xl mx-auto px-5 py-8">
        {books.length > 0 && (
          <div className="mb-8">
            <h2 className="font-display font-semibold text-lg mb-3">{ui.bookHeading}</h2>
            <div className="flex flex-wrap gap-2">
              {books.map((b, i) => (
                <a key={i} href={b.url} target="_blank" rel="noreferrer"
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${b.language === lang ? "bg-primary text-primary-foreground hover:bg-primary/90 ring-2 ring-primary/40" : "bg-muted text-foreground/80 hover:bg-accent"}`}
                  data-testid={`public-book-${b.language}`}>
                  {FLAG[b.language] || "📖"} {LANGS[b.language] || b.language.toUpperCase()}
                  {b.language === lang && <span className="text-[10px] font-semibold uppercase opacity-80">· {ui.yourBook}</span>}
                  <ExternalLink className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        )}

        {secs.length > 0 && (
          <div>
            <div className="space-y-6">
              {secs.map((s, i) => (
                <div key={i} className="bg-card rounded-xl border border-border p-6 shadow-sm">
                  <h3 className="font-display font-semibold text-lg text-secondary mb-2">{s.title}</h3>
                  <p className="text-sm text-foreground/90 whitespace-pre-line leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {books.length === 0 && secs.length === 0 && !data.welcome_hidden && (
          <p className="text-muted-foreground text-center py-10">{ui.notReady}</p>
        )}

        <div className="mt-10" id="guest-chat-section" data-testid="guest-chat">
          <h2 className="font-display font-semibold text-lg mb-3 flex items-center gap-2"><MessageCircle className="h-5 w-5 text-primary" /> {ui.chatTitle}</h2>
          <div className="bg-card rounded-xl border border-border shadow-sm flex flex-col overflow-hidden">
            <div ref={chatBoxRef} className="p-4 space-y-3 max-h-[420px] overflow-y-auto bg-gradient-to-br from-primary/15 via-background to-background min-h-[140px] text-base" data-testid="guest-chat-box">
              {intake === null && isGuestBooking && chat !== null && renderWizard()}
              {chat === null ? (
                <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : chat.length === 0 ? (
                (intake === null && isGuestBooking) ? null : <p className="text-sm text-muted-foreground text-center py-6">{ui.chatEmpty}</p>
              ) : chat.map((m) => {
                const mine = m.sender === "guest";
                const att = m.attachment;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`guest-chat-msg-${m.id}`}>
                    <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-base ${mine ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-card border border-border rounded-bl-sm"}`}>
                      {att && (att.kind === "image" ? (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}${att.url}`} target="_blank" rel="noreferrer" data-testid={`guest-chat-att-${m.id}`}>
                          <img src={`${process.env.REACT_APP_BACKEND_URL}${att.url}?w=800`} loading="lazy" decoding="async" alt={att.filename} className="rounded-lg max-h-52 max-w-full mb-1 object-cover" />
                        </a>
                      ) : (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}${att.url}`} target="_blank" rel="noreferrer" className={`flex items-center gap-2 mb-1 underline ${mine ? "text-primary-foreground" : "text-secondary"}`} data-testid={`guest-chat-att-${m.id}`}>
                          <FileText className="h-4 w-4 shrink-0" /> <span className="truncate">{att.filename}</span>
                        </a>
                      ))}
                      {m.text && <p className="whitespace-pre-line break-words">{m.text}</p>}
                      {m.text && (
                        <div className="mt-1">
                          <button type="button" onClick={() => translateMsg(m.id)} disabled={xlate[m.id]?.loading}
                            className={`inline-flex items-center gap-1 text-[11px] font-medium ${mine ? "text-primary-foreground/80 hover:text-primary-foreground" : "text-secondary hover:text-secondary/80"}`}
                            data-testid={`guest-translate-${m.id}`}>
                            {xlate[m.id]?.loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Languages className="h-3 w-3" />}
                            {xlate[m.id]?.show ? ui.showOriginal : ui.translate}
                          </button>
                          {xlate[m.id]?.show && xlate[m.id]?.text && (
                            <p className={`mt-1 pt-1 border-t whitespace-pre-line break-words italic ${mine ? "border-white/25 text-primary-foreground/90" : "border-border text-muted-foreground"}`} data-testid={`guest-translation-${m.id}`}>{xlate[m.id].text}</p>
                          )}
                        </div>
                      )}
                      <p className={`text-[10px] mt-1 ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{mine ? ui.you : (m.sender_name || ui.team)} · {fmtChatTime(m.created_at)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            {!(intake === null && isGuestBooking) && (
            <div className="p-3 border-t border-border flex items-center gap-2">
              <input ref={chatAttachRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp,application/pdf" className="hidden" onChange={uploadChatAttachment} data-testid="guest-chat-attach-input" />
              <button type="button" onClick={() => chatAttachRef.current?.click()} disabled={chatUploading} className="h-10 w-10 grid place-items-center rounded-md border border-border text-muted-foreground hover:bg-accent disabled:opacity-50 shrink-0" data-testid="guest-chat-attach-btn">
                {chatUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
              </button>
              <input
                value={chatText}
                onChange={(e) => setChatText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                placeholder={ui.placeholder}
                className="flex-1 h-10 rounded-md border border-border bg-background px-3 text-base outline-none focus:ring-1 focus:ring-ring"
                data-testid="guest-chat-input"
              />
              <button onClick={sendChat} disabled={chatSending || !chatText.trim()} className="h-10 w-10 grid place-items-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 shrink-0" data-testid="guest-chat-send">
                {chatSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
            )}
          </div>
        </div>
      </main>

      <div className="max-w-3xl mx-auto px-5 pb-8">
        <WhatsAppContact text={ui.waText} highlight />
      </div>
      <footer className="max-w-3xl mx-auto px-5 py-8 text-sm text-muted-foreground text-center border-t border-border">
        {ui.footer}
      </footer>
    </div>
  );
}
